import React, { useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaLinkedin, FaGithub } from "react-icons/fa";
import "./Contact.css";

// Configuración de Brevo
const BREVO_CONFIG = {
  apiKey: "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",
  senderEmail: "info@oscaralvarez.tech", // Email verificado en Brevo temporalmente
  senderName: "Oscar Alvarez",
};

const Contact: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      // Validar que todos los campos estén completos
      if (!formData.name || !formData.email || !formData.subject || !formData.message) {
        throw new Error("Por favor, completa todos los campos");
      }

      // Validar formato de email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        throw new Error("Por favor, ingresa un email válido");
      }

      // Verificar que la API Key tenga el formato correcto
      if (!BREVO_CONFIG.apiKey || BREVO_CONFIG.apiKey === "tu_api_key_de_brevo_aqui") {
        throw new Error("API Key de Brevo no configurada correctamente");
      }

      // Enviar email usando Brevo
      const emailData = {
        sender: {
          name: BREVO_CONFIG.senderName,
          email: BREVO_CONFIG.senderEmail,
        },
        to: [
          {
            email: "oscaralvarez4c@gmail.com", // Tu email donde recibirás los mensajes
            name: "Oscar Alvarez",
          },
        ],
        subject: `Nuevo mensaje de contacto: ${formData.subject}`,
        htmlContent: `
          <html>
            <body>
              <h2>Nuevo mensaje de contacto desde tu portfolio</h2>
              <p><strong>Nombre:</strong> ${formData.name}</p>
              <p><strong>Email:</strong> ${formData.email}</p>
              <p><strong>Asunto:</strong> ${formData.subject}</p>
              <p><strong>Mensaje:</strong></p>
              <p>${formData.message.replace(/\n/g, "<br>")}</p>
              <hr>
              <p><em>Mensaje enviado desde oscaralvarez.tech</em></p>
            </body>
          </html>
        `,
        textContent: `
          Nuevo mensaje de contacto desde tu portfolio
          
          Nombre: ${formData.name}
          Email: ${formData.email}
          Asunto: ${formData.subject}
          Mensaje: ${formData.message}
          
          ---
          Mensaje enviado desde oscaralvarez.tech
        `,
      };

      console.log("Enviando email con datos:", emailData);
      console.log("API Key:", BREVO_CONFIG.apiKey.substring(0, 20) + "...");
      console.log("Remitente:", BREVO_CONFIG.senderEmail);
      console.log("Destinatario: oscaralvarez4c@gmail.com");

      const response = await fetch("https://api.brevo.com/v3/smtp/email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "api-key": BREVO_CONFIG.apiKey,
          accept: "application/json",
        },
        body: JSON.stringify(emailData),
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error("Brevo API Error:", response.status, errorData);
        throw new Error(`Error al enviar el mensaje: ${response.status} - ${errorData}`);
      }

      // Verificar respuesta exitosa
      const responseData = await response.json();
      console.log("Brevo API Success:", responseData);

      // Éxito
      setSubmitStatus("success");
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
      });

      // Resetear estado después de 5 segundos
      setTimeout(() => {
        setSubmitStatus("idle");
      }, 5000);
    } catch (error) {
      console.error("Error al enviar mensaje:", error);
      setSubmitStatus("error");

      // Resetear estado de error después de 5 segundos
      setTimeout(() => {
        setSubmitStatus("idle");
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <FaEnvelope />,
      title: "Email",
      value: "oscaralvarez4c@gmail.com",
      link: "mailto:oscaralvarez4c@gmail.com",
    },
    {
      icon: <FaPhone />,
      title: "Teléfono",
      value: "+34 600 23 57 14",
      link: "tel:+34600235714",
    },
    {
      icon: <FaMapMarkerAlt />,
      title: "Ubicación",
      value: "Galicia, España",
      link: null,
    },
  ];

  const socialLinks = [
    {
      icon: <FaLinkedin />,
      name: "LinkedIn",
      url: "https://linkedin.com/in/oscaralvarezrua",
      color: "#0077b5",
    },
    {
      icon: <FaGithub />,
      name: "GitHub",
      url: "https://github.com/oscaralvarezrua",
      color: "#333",
    },
    {
      icon: <FaEnvelope />,
      name: "Email",
      url: "mailto:oscaralvarez4c@gmail.com",
      color: "#b3ade0",
    },
  ];

  return (
    <section id="contact" className="contact">
      <div className="contact-container">
        <motion.div ref={ref} className="contact-content" initial={{ opacity: 0, y: 50 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8 }}>
          <motion.h2 className="section-title" initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.2 }}>
            Contáctame
          </motion.h2>

          <motion.p className="contact-description" initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.4 }}>
            ¿Tienes un proyecto en mente? ¿Quieres colaborar? ¡Me encantaría escuchar de ti!
          </motion.p>

          <div className="contact-grid">
            <motion.div className="contact-info" initial={{ opacity: 0, x: -50 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.8, delay: 0.6 }}>
              <h3>Información de Contacto</h3>

              <div className="info-items">
                {contactInfo.map((info, index) => (
                  <motion.div key={info.title} className="info-item" initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.8 + index * 0.1 }}>
                    <div className="info-icon">{info.icon}</div>
                    <div className="info-content">
                      <h4>{info.title}</h4>
                      {info.link ? (
                        <a href={info.link} className="info-link">
                          {info.value}
                        </a>
                      ) : (
                        <p>{info.value}</p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="social-links">
                <h4>Sígueme</h4>
                <div className="social-icons">
                  {socialLinks.map((social, index) => (
                    <motion.a
                      key={social.name}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="social-icon"
                      style={{ "--social-color": social.color } as React.CSSProperties}
                      initial={{ opacity: 0, scale: 0 }}
                      animate={inView ? { opacity: 1, scale: 1 } : {}}
                      transition={{ duration: 0.5, delay: 1.2 + index * 0.1 }}
                      whileHover={{ scale: 1.1 }}
                      aria-label={`Visitar ${social.name}`}
                    >
                      {social.icon}
                    </motion.a>
                  ))}
                </div>
              </div>
            </motion.div>

            <motion.div className="contact-form" initial={{ opacity: 0, x: 50 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.8, delay: 0.6 }}>
              <h3>Envíame un Mensaje</h3>

              <form onSubmit={handleSubmit} className="form">
                <div className="form-group">
                  <label htmlFor="name">Nombre *</label>
                  <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} required placeholder="Tu nombre completo" />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} required placeholder="tu@email.com" />
                </div>

                <div className="form-group">
                  <label htmlFor="subject">Asunto *</label>
                  <input type="text" id="subject" name="subject" value={formData.subject} onChange={handleInputChange} required placeholder="¿En qué puedo ayudarte?" />
                </div>

                <div className="form-group">
                  <label htmlFor="message">Mensaje *</label>
                  <textarea id="message" name="message" value={formData.message} onChange={handleInputChange} required rows={5} placeholder="Cuéntame sobre tu proyecto..." />
                </div>

                <button type="submit" className="btn btn-primary submit-btn" disabled={isSubmitting}>
                  {isSubmitting ? "Enviando..." : "Enviar Mensaje"}
                </button>

                {submitStatus === "success" && (
                  <motion.div className="success-message" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                    ¡Mensaje enviado exitosamente! Te responderé pronto.
                  </motion.div>
                )}

                {submitStatus === "error" && (
                  <motion.div className="error-message" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                    Hubo un error al enviar el mensaje. Por favor, intenta de nuevo.
                  </motion.div>
                )}
              </form>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
